import { Component, Input } from '@angular/core';
import { combineInput, formatDuration } from '@app/new-ui/analysis/analysis.component';
import { generateTimelineChartLines } from '@app/new-ui/chart-transforms';
import { chart } from '@lib/angular/chart';
import { DateTime } from '@lib/date-time';
import { op } from '@lib/rxjs';
import { BehaviorSubject, combineLatest, firstValueFrom, Observable } from 'rxjs';
import { FragQueryService } from 'src/injectables/frag-query.service';
import * as Highcharts from 'highcharts/highstock';
import { VehicleQueryService } from 'src/injectables/vehicle-query.service';
import { fragQuery } from '@models';

@Component({
    selector: 'report-page-timeline',
    templateUrl: './timeline-page.component.html',
    styleUrls: ['./timeline-page.component.scss'],
})
export class ReportTimelinePageComponent {
    constructor(
        public fragQuery: FragQueryService,
        private readonly vehicleQuery: VehicleQueryService
    ) { }

    @Input() timelineData: chart.line.Series[];
    @Input() startDate: DateTime;
    @Input() endDate: DateTime;

    public readonly particleSizeUnit$ = this.fragQuery.particleSizeUnit$;
    public readonly currentInterval = new BehaviorSubject(null);
    public readonly chartType$ = this.fragQuery.chartType$;
    public Highcharts: typeof Highcharts = Highcharts;
    public chartOptions: Highcharts.Options;
    public readonly device$ = this.fragQuery.device$;
    public efficiency: number = 0
    public offBeltDuration: string = "0s"
    public blackBeltDuration: string = "0s"
    public totalUpTime: string = "0s"
    public beltOffPercentage: number = 0;
    public blackBeltPercentage: number = 0;
    addData(type: string) {
        this.fragQuery.addDataAt.next(type);
    }

    async onPlotClick(time: number) {
        const input = await firstValueFrom(
            this.fragQuery.timelineSieveSizesAtPercents$
        );
        let dataPoint;
        for (let index = 0; index < input.intervals.length; index++) {
            const interval = input.intervals[index];
            if (interval.begin.valueOf() <= time) {
                dataPoint = interval;
            } else {
                break;
            }
        }
        if (dataPoint) {
            this.currentInterval.next(dataPoint);
        }
    }

    private _calculateBeltTimeMetrics(input: fragQuery.timeline.sieveSizesAtPercents.Response): void {
        const totalTime = input?.totalTime || 0;
        const offBeltDuration = input?.offBeltDuration || 0;
        const blackBeltDuration = input?.blackBeltDuration || 0;

        this.efficiency = input?.efficiency ?? 0;
        this.beltOffPercentage = +(100 * offBeltDuration / totalTime).toFixed(2) || 0;
        this.blackBeltPercentage = +(100 * blackBeltDuration / totalTime).toFixed(2) || 0;

        this.totalUpTime = formatDuration(totalTime - offBeltDuration - blackBeltDuration);
        this.blackBeltDuration = formatDuration(blackBeltDuration);
        this.offBeltDuration = formatDuration(offBeltDuration);
    }

    public readonly chartLines$: Observable<Highcharts.Options> = combineLatest([
        this.fragQuery.newPercents$,
        this.fragQuery.timelineSieveSizesAtPercents$,
        this.fragQuery.addOnTimelineSieveSizesAtPercents$,
        this.fragQuery.particleSizeUnit$,
        this.fragQuery.muckpiles$,
        this.vehicleQuery.vehicles$,
        this.device$
    ]).pipe(
        op.tap(([percents, input, addOn]) =>
            combineInput(input, addOn, this.fragQuery.addDataAt.value)
        ),
        op.tap((input) => {
            if (input[1] && input[1].intervals) {
                this.fragQuery.totalIntervalLength.next(
                    input[1].end.valueOf() - input[1].begin.valueOf()
                );
                this.fragQuery.intervalsLength.next(input[1].intervals.length);
            }
        }),
        op.map(([percents, input, input2, unit, muckpiles, vehicles, device]) => {
            const timelineChartConfig = {
                chartType: 'line' as const,
                percents,
                input,
                particleSizeUnit: unit,
                zoomingEnable: false,
                navigatorEnable: false,
                tooltipEnable: false,
                scrollbarEnable: false,
                addData: this.addData.bind(this),
                plotClick: this.onPlotClick.bind(this),
                muckpiles,
                vehicles,
                device
            }
            this._calculateBeltTimeMetrics(input);
            return generateTimelineChartLines(timelineChartConfig);
        })
    );

    public readonly chartSeries$: Observable<Highcharts.Options> = combineLatest([
        this.fragQuery.newPercents$,
        this.fragQuery.timelineSieveSizesAtPercents$,
        this.fragQuery.addOnTimelineSieveSizesAtPercents$,
        this.fragQuery.particleSizeUnit$,
    ]).pipe(
        op.tap(([percents, input, addOn]) =>
            combineInput(input, addOn, this.fragQuery.addDataAt.value)
        ),
        op.tap((input) => {
            if (input[1] && input[1].intervals) {
                this.fragQuery.totalIntervalLength.next(
                    input[1].end.valueOf() - input[1].begin.valueOf()
                );
                this.fragQuery.intervalsLength.next(input[1].intervals.length);
            }
        }),
        op.map(([percents, input, input2, unit]) => {
            const timelineChartConfig = {
                chartType: 'series' as const,
                percents,
                input,
                particleSizeUnit: unit,
                zoomingEnable: false,
                navigatorEnable: false,
                tooltipEnable: false,
                scrollbarEnable: false,
                addData: this.addData.bind(this),
                plotClick: this.onPlotClick.bind(this),
            }
            return generateTimelineChartLines(timelineChartConfig);
        })
    );
}